/**
 * Raid-warning banner (WoW-style, Aion-themed): when a boss starts a signature / deadly cast, or
 * any nearby enemy starts a cast with an authored deadly warning, a short centred call-out
 * appears above the combat area: "Ritual of the Fang — SOAK!" with the spell icon and the
 * authored sentence underneath. Deadly reads red, danger orange; never full-screen flashes.
 *
 * Text and severity come from `EnemyAbility.warning` / `counter` (render/toon/fx/mechanics/warnings.ts);
 * `WarningFeed` decides which call-out holds the banner. Per frame: one pass over the units
 * looking for new cast starts (Map write only when a cast starts), DOM touched only on change.
 */
import type { UnitId } from '@keystone/sim';
import { hudNow } from '../game/prediction';
import { type CastWarning, WarningFeed, bannerText, castWarningFor } from '../render/toon/fx/mechanics/warnings';
import { el } from './dom';
import { iconUrl } from './icons';
import type { HudUpdate } from './hud';
import type { HudWidget, HudWidgetDef } from './widgets';
import './bossWarning.css';

/** Non-boss casts only raise the banner within this range of the player (yd). */
const TRASH_RANGE = 40;
/** Boss casts within this range (the whole arena). */
const BOSS_RANGE = 80;

export class BossWarning implements HudWidget {
  readonly root: HTMLElement;
  private readonly icon: HTMLImageElement;
  private readonly main: HTMLElement;
  private readonly sub: HTMLElement;
  private readonly feed = new WarningFeed();
  /** Last seen cast start per unit (a new startedAt = a new cast). */
  private readonly seen = new Map<UnitId, number>();
  private shown: CastWarning | null = null;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'bw-banner', parent);
    this.root.setAttribute('role', 'alert');
    const row = el('div', 'bw-row', this.root);
    this.icon = el('img', 'bw-icon', row);
    this.icon.alt = '';
    this.icon.draggable = false;
    this.main = el('div', 'bw-main', row);
    this.sub = el('div', 'bw-sub', this.root);
  }

  update(h: HudUpdate): void {
    const { world, content } = h;
    const now = hudNow(world);
    // Spectating has no player: measure range from the followed unit (session.ts followId).
    const meId = h.playerId ?? world.partyIds[0] ?? null;
    const me = meId ? world.units[meId] : undefined;
    if (me) {
      for (const id of world.unitOrder) {
        const u = world.units[id];
        if (!u || u.faction === 'party') continue;
        const c = u.alive ? u.cast : null;
        if (!c) {
          if (this.seen.has(id)) this.seen.delete(id);
          continue;
        }
        if (this.seen.get(id) === c.startedAt) continue;
        this.seen.set(id, c.startedAt);
        const boss = u.tier === 'boss';
        const d = Math.hypot(u.pos.x - me.pos.x, u.pos.y - me.pos.y);
        if (d > (boss ? BOSS_RANGE : TRASH_RANGE)) continue;
        const w = castWarningFor(content, c.abilityId, boss);
        // Bosses: danger and deadly casts. Trash: authored deadly call-outs only.
        if (!w || w.severity === 'info' || (!boss && w.severity !== 'deadly')) continue;
        this.feed.push(w, c.abilityId, now, c.endsAt);
      }
    }
    const cur = this.feed.current(now);
    if (cur === this.shown) return;
    this.shown = cur;
    if (!cur) {
      this.root.classList.remove('show');
      return;
    }
    const abilityId = this.feed.shownAbilityId ?? '';
    const name = content.ability(abilityId)?.name ?? cur.text;
    const t = bannerText(name, cur);
    this.main.textContent = t.main;
    this.sub.textContent = t.sub ?? '';
    this.sub.hidden = !t.sub;
    const url = iconUrl(abilityId, 128);
    if (url) this.icon.src = url;
    this.icon.hidden = !url;
    this.root.dataset['severity'] = cur.severity;
    this.root.style.setProperty('--bw-color', cur.color);
    // Restart the entrance animation.
    this.root.classList.remove('show');
    void this.root.offsetWidth;
    this.root.classList.add('show');
  }

  /** Between runs / on wipe. */
  clear(): void {
    this.feed.clear();
    this.seen.clear();
    this.shown = null;
    this.root.classList.remove('show');
  }

  dispose(): void {
    this.root.remove();
  }
}

/** Widget registration (hud/widgets.ts). The banner lives on the HUD root (screen space). */
export const bossWarningWidget: HudWidgetDef = {
  id: 'bossWarning',
  region: 'target',
  create: (_host, ctx) => new BossWarning(ctx.root),
};
